import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/apiClient';
import type { Lead, ApiResponse, PaginatedResponse } from '@/types/api';
import toast from 'react-hot-toast';
import { USE_MOCKS } from '@/lib/mockConfig';
import { mockLeads } from '@/lib/mockData';

const filterMockLeads = (filters: { status?: string; platform?: string; priority?: string; search?: string }) => {
  const term = (filters.search || '').trim().toLowerCase();
  return (mockLeads as unknown as Lead[]).filter((l) => {
    if (filters.status && (l as any).status !== filters.status) return false;
    if (filters.platform && (l as any).platform !== filters.platform) return false;
    if (filters.priority && (l as any).priority !== filters.priority) return false;
    if (term && !`${(l as any).customerName} ${(l as any).message}`.toLowerCase().includes(term)) return false;
    return true;
  });
};

interface LeadsFilters {
  status?: string;
  platform?: string;
  priority?: string;
  dateFrom?: string;
  dateTo?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export const useLeads = (filters: LeadsFilters = {}) => {
  return useQuery({
    queryKey: ['leads', filters],
    queryFn: () => {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== '') {
          params.append(key, value.toString());
        }
      });
      
      const mockFallback = () => {
        const leads = filterMockLeads(filters);
        return { leads, total: leads.length, pages: 1 };
      };

      // GET /api/leads returns a FLAT body: { success, data: Lead[], pagination }.
      // (Not a nested PaginatedResponse — the array and pagination sit side by side.)
      return apiClient.get<ApiResponse<Lead[]>>(`/api/leads?${params}`)
        .then(res => {
          const body = res.data as ApiResponse<Lead[]> & {
            pagination?: PaginatedResponse<Lead>['pagination'];
          };
          const leads = body.data || [];
          // While the backend has no data yet, show mock leads so the UI is reviewable.
          if (USE_MOCKS && leads.length === 0) return mockFallback();
          return {
            leads,
            total: body.pagination?.total ?? leads.length,
            pages: body.pagination?.totalPages ?? 1,
          };
        })
        .catch((err) => {
          if (USE_MOCKS) return mockFallback();
          throw err;
        });
    },
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: 60 * 1000, // Refetch every minute
  });
};

export const useLeadActions = () => {
  const queryClient = useQueryClient();

  const updateLead = useMutation({
    mutationFn: (data: { id: string; updates: Partial<Lead> }) =>
      apiClient.patch<ApiResponse<Lead>>(`/api/leads/${data.id}`, data.updates)
        .then(res => res.data.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      toast.success('Lead updated successfully');
    },
    onError: () => {
      toast.error('Failed to update lead');
    },
  });

  const deleteLead = useMutation({
    mutationFn: (leadId: string) =>
      apiClient.delete(`/api/leads/${leadId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      toast.success('Lead deleted successfully');
    },
    onError: () => {
      toast.error('Failed to delete lead');
    },
  });

  const bulkUpdateLeads = useMutation({
    mutationFn: (data: { leadIds: string[]; updates: Partial<Lead> }) =>
      apiClient.patch<ApiResponse<{ updated: number; failed: string[] }>>('/api/leads/bulk/update', data)
        .then(res => res.data.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      toast.success('Leads updated successfully');
    },
    onError: () => {
      toast.error('Failed to update leads');
    },
  });

  return {
    updateLead,
    deleteLead,
    bulkUpdateLeads
  };
};

// Derive a stats summary from the mock leads so the tiles aren't all-zero while
// the DB is empty (keeps the demo coherent with the mock list above).
const mockLeadStats = () => {
  const leads = mockLeads as unknown as Lead[];
  const countStatus = (s: string) => leads.filter((l) => (l as any).status === s).length;
  const total = leads.length;
  const converted = countStatus('converted');
  return {
    total,
    new: countStatus('new'),
    contacted: countStatus('contacted'),
    qualified: countStatus('qualified'),
    converted,
    conversionRate: total ? Number(((converted / total) * 100).toFixed(1)) : 0,
    avgResponseTime: 1.8,
  };
};

export const useLeadStats = () => {
  return useQuery({
    queryKey: ['leads', 'stats'],
    // GET /api/leads/stats/summary returns { overview: {...}, breakdown: { byStatus[] } }
    // — not flat totals — so map overview + derive per-status counts from breakdown.
    queryFn: () =>
      apiClient.get<ApiResponse<any>>('/api/leads/stats/summary')
        .then(res => {
          const d = res.data.data || {};
          const overview = d.overview || {};
          const byStatus: Array<{ status: string; count: number }> = d.breakdown?.byStatus || [];
          const statusCount = (s: string) => byStatus.find((x) => x.status === s)?.count || 0;
          const total = overview.totalLeads || 0;

          if (USE_MOCKS && total === 0) return mockLeadStats() as any;

          return {
            total,
            new: overview.newLeads ?? statusCount('new'),
            contacted: statusCount('contacted'),
            qualified: statusCount('qualified'),
            converted: overview.convertedLeads ?? statusCount('converted'),
            conversionRate: parseFloat(overview.conversionRate) || 0,
            avgResponseTime: 0, // not yet provided by the API
          } as any;
        })
        .catch((err) => {
          if (USE_MOCKS) return mockLeadStats() as any;
          throw err;
        }),
    staleTime: 60 * 1000, // 1 minute
    refetchInterval: 2 * 60 * 1000, // Refetch every 2 minutes
  });
};
